import { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronLeft, Plus, Edit2, Trash2, AlertTriangle, X, Save, Layout, Users, List, Move } from 'lucide-react';
import { api } from '../../api/client';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { useEscapeToClose } from '../../hooks/useEscapeToClose';

type Zone = {
  id: string;
  tenantId: string;
  locationId: string;
  name: string;
  code: string | null;
  sortOrder: number;
  isActive: boolean;
  tableCount: number;
  createdAt: string;
};

type Table = {
  id: string;
  tenantId: string;
  locationId: string;
  zoneId: string;
  name: string;
  tableNumber: string;
  capacity: number;
  minCapacity: number | null;
  shape: string;
  posX: string;
  posY: string;
  width: string;
  height: string;
  rotation: string;
  status: string;
  serviceMode: string;
  isActive: boolean;
};

type Location = { id: string; name: string; city: string | null };

type ViewMode = 'list' | 'visual';

export function LocationLayoutPage() {
  const { id: locationId } = useParams<{ id: string }>();
  const [location, setLocation] = useState<Location | null>(null);
  const [zones, setZones] = useState<Zone[]>([]);
  const [tables, setTables] = useState<Table[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<ViewMode>('list');
  const [activeZoneId, setActiveZoneId] = useState<string | null>(null);

  const [editingZone, setEditingZone] = useState<Zone | null>(null);
  const [creatingZone, setCreatingZone] = useState(false);
  const [deleteZoneTarget, setDeleteZoneTarget] = useState<Zone | null>(null);

  const [editingTable, setEditingTable] = useState<Table | null>(null);
  const [creatingTable, setCreatingTable] = useState(false);
  const [deleteTableTarget, setDeleteTableTarget] = useState<Table | null>(null);

  const refresh = useCallback(async () => {
    if (!locationId) return;
    setLoading(true);
    setError(null);
    try {
      const [loc, z, t] = await Promise.all([
        api.get<Location>(`/admin/locations/${locationId}`),
        api.get<Zone[]>(`/admin/locations/${locationId}/zones`),
        api.get<Table[]>(`/admin/locations/${locationId}/tables`),
      ]);
      setLocation(loc);
      setZones(z);
      setTables(t);
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Greška');
    } finally {
      setLoading(false);
    }
  }, [locationId]);

  useEffect(() => { refresh(); }, [refresh]);

  // Select first zone by default when entering visual mode
  useEffect(() => {
    if (view === 'visual' && !activeZoneId && zones.length > 0) {
      setActiveZoneId(zones[0].id);
    }
  }, [view, activeZoneId, zones]);

  // Optimistic local position update with server sync
  const updateTablePosition = useCallback(async (tableId: string, posX: number, posY: number) => {
    setTables((prev) => prev.map((t) => t.id === tableId ? { ...t, posX: String(posX), posY: String(posY) } : t));
    try {
      await api.patch(`/admin/locations/${locationId}/tables/${tableId}/position`, { posX, posY });
    } catch (e) {
      // Revert by refetching on error
      refresh();
    }
  }, [locationId, refresh]);

  const confirmDeleteZone = async () => {
    if (!deleteZoneTarget) return;
    await api.del(`/admin/locations/${locationId}/zones/${deleteZoneTarget.id}`);
    refresh();
  };

  const confirmDeleteTable = async () => {
    if (!deleteTableTarget) return;
    await api.del(`/admin/locations/${locationId}/tables/${deleteTableTarget.id}`);
    refresh();
  };

  const tablesByZone = (zoneId: string) => tables.filter((t) => t.zoneId === zoneId);

  return (
    <div>
      <Link to="/locations" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--color-text-muted)', fontSize: 12, marginBottom: 12, textDecoration: 'none' }}>
        <ChevronLeft size={14} /> Lokacije
      </Link>

      <header style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 16 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: 'var(--color-text-strong)' }}>
            <Layout size={20} style={{ verticalAlign: 'middle', marginRight: 8 }} />
            Layout: {location?.name || 'Učitavanje...'}
          </h1>
          <div style={{ marginTop: 4, fontSize: 13, color: 'var(--color-text-muted)' }}>
            Restoran zone i stolovi za ovu lokaciju
          </div>
        </div>
        <div style={{ flex: 1 }} />
        <div style={viewToggleBar}>
          <button
            onClick={() => setView('list')}
            style={{ ...viewToggleBtn, ...(view === 'list' ? viewToggleBtnActive : {}) }}
            title="Tabelarni prikaz"
          >
            <List size={13} /> Lista
          </button>
          <button
            onClick={() => setView('visual')}
            style={{ ...viewToggleBtn, ...(view === 'visual' ? viewToggleBtnActive : {}) }}
            title="Vizuelni raspored (drag-drop)"
          >
            <Move size={13} /> Raspored
          </button>
        </div>
        <button onClick={() => setCreatingZone(true)} style={btnSecondary}>
          <Plus size={14} /> Nova zona
        </button>
        <button onClick={() => setCreatingTable(true)} style={btnPrimary} disabled={zones.length === 0}>
          <Plus size={14} /> Novi sto
        </button>
      </header>

      {error && (
        <div style={errorBox}>
          <AlertTriangle size={14} /> {error}
        </div>
      )}

      {!loading && zones.length === 0 && (
        <div style={{ background: 'var(--color-bg-surface)', borderRadius: 10, border: '1px solid var(--color-border)', padding: 40, textAlign: 'center', color: 'var(--color-text-muted)' }}>
          <Layout size={32} style={{ margin: '0 auto 8px', opacity: 0.3 }} />
          <div>Nema definisanih zona za ovu lokaciju</div>
          <button onClick={() => setCreatingZone(true)} style={{ ...btnPrimary, marginTop: 12 }}>
            <Plus size={14} /> Kreiraj prvu zonu
          </button>
        </div>
      )}

      {view === 'visual' && zones.length > 0 && (
        <FloorPlanEditor
          zones={zones}
          tables={tables}
          activeZoneId={activeZoneId}
          onSelectZone={setActiveZoneId}
          onMoveTable={updateTablePosition}
          onEditTable={setEditingTable}
        />
      )}

      {/* Zone sa stolovima (list view) */}
      {view === 'list' && <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {zones.map((zone) => {
          const zoneTables = tablesByZone(zone.id);
          return (
            <div key={zone.id} style={{ background: 'var(--color-bg-surface)', borderRadius: 10, border: '1px solid var(--color-border)', overflow: 'hidden' }}>
              <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--color-border)', display: 'flex', alignItems: 'center', gap: 12, background: 'var(--color-bg)' }}>
                <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: 'var(--color-text-strong)' }}>
                  {zone.name}
                </h3>
                {zone.code && <span style={{ fontSize: 11, fontFamily: 'monospace', color: 'var(--color-text-muted)' }}>({zone.code})</span>}
                <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>· {zoneTables.length} stolova</span>
                {!zone.isActive && <span style={badgeInactive}>Neaktivna</span>}
                <div style={{ flex: 1 }} />
                <button onClick={() => setEditingZone(zone)} style={iconBtn} title="Izmeni zonu"><Edit2 size={13} /></button>
                <button onClick={() => setDeleteZoneTarget(zone)} style={{ ...iconBtn, color: '#fca5a5' }} title="Obriši zonu"><Trash2 size={13} /></button>
              </div>

              {zoneTables.length === 0 ? (
                <div style={{ padding: 20, textAlign: 'center', color: 'var(--color-text-muted)', fontSize: 12 }}>
                  Nema stolova u ovoj zoni
                </div>
              ) : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ background: 'var(--color-bg-surface)' }}>
                        <th style={th}>Broj</th>
                        <th style={th}>Naziv</th>
                        <th style={{ ...th, textAlign: 'right' }}>Kapacitet</th>
                        <th style={th}>Oblik</th>
                        <th style={th}>Service mode</th>
                        <th style={th}>Status</th>
                        <th style={{ ...th, width: 100, textAlign: 'right' }}>Akcije</th>
                      </tr>
                    </thead>
                    <tbody>
                      {zoneTables.map((t) => (
                        <tr key={t.id}>
                          <td style={{ ...td, fontFamily: 'monospace', fontWeight: 700, color: 'var(--color-text-strong)' }}>{t.tableNumber}</td>
                          <td style={{ ...td, color: 'var(--color-text)' }}>{t.name}</td>
                          <td style={{ ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                            <Users size={11} style={{ verticalAlign: 'middle', marginRight: 4, opacity: 0.6 }} />
                            {t.capacity}
                            {t.minCapacity && <span style={{ color: 'var(--color-text-muted)', fontSize: 11 }}> (min {t.minCapacity})</span>}
                          </td>
                          <td style={{ ...td, color: 'var(--color-text-muted)', fontSize: 12 }}>{t.shape}</td>
                          <td style={{ ...td, color: 'var(--color-text-muted)', fontSize: 12 }}>{t.serviceMode}</td>
                          <td style={td}>
                            {t.isActive ? <span style={statusBadge(t.status)}>{t.status}</span> : <span style={badgeInactive}>Neaktivan</span>}
                          </td>
                          <td style={{ ...td, textAlign: 'right' }}>
                            <button onClick={() => setEditingTable(t)} style={iconBtn}><Edit2 size={13} /></button>
                            <button onClick={() => setDeleteTableTarget(t)} style={{ ...iconBtn, color: '#fca5a5' }}><Trash2 size={13} /></button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        })}
      </div>}

      {/* Zone form dialog */}
      {(creatingZone || editingZone) && (
        <ZoneFormDialog
          zone={editingZone}
          onClose={() => { setCreatingZone(false); setEditingZone(null); }}
          onSaved={() => { setCreatingZone(false); setEditingZone(null); refresh(); }}
          locationId={locationId!}
        />
      )}

      {/* Table form dialog */}
      {(creatingTable || editingTable) && (
        <TableFormDialog
          table={editingTable}
          zones={zones}
          onClose={() => { setCreatingTable(false); setEditingTable(null); }}
          onSaved={() => { setCreatingTable(false); setEditingTable(null); refresh(); }}
          locationId={locationId!}
        />
      )}

      {deleteZoneTarget && (
        <ConfirmDialog
          title="Obriši zonu"
          message={
            deleteZoneTarget.tableCount > 0
              ? `Zona "${deleteZoneTarget.name}" sadrži ${deleteZoneTarget.tableCount} stolova. Prebaci ih u drugu zonu ili obriši pre nego što obrišeš zonu.`
              : `Da li želite da obrišete zonu "${deleteZoneTarget.name}"?`
          }
          confirmLabel="Obriši"
          variant="danger"
          onConfirm={confirmDeleteZone}
          onClose={() => setDeleteZoneTarget(null)}
        />
      )}

      {deleteTableTarget && (
        <ConfirmDialog
          title="Obriši sto"
          message={`Da li želite da obrišete sto "${deleteTableTarget.name}" (${deleteTableTarget.tableNumber})? Ako sto ima istoriju sesija, brisanje neće proći - deaktiviraj ga umesto toga.`}
          confirmLabel="Obriši"
          variant="danger"
          onConfirm={confirmDeleteTable}
          onClose={() => setDeleteTableTarget(null)}
        />
      )}
    </div>
  );
}

function FloorPlanEditor({
  zones,
  tables,
  activeZoneId,
  onSelectZone,
  onMoveTable,
  onEditTable,
}: {
  zones: Zone[];
  tables: Table[];
  activeZoneId: string | null;
  onSelectZone: (id: string) => void;
  onMoveTable: (tableId: string, posX: number, posY: number) => void;
  onEditTable: (t: Table) => void;
}) {
  const canvasRef = useRef<HTMLDivElement | null>(null);
  const dragStateRef = useRef<{ tableId: string; offsetX: number; offsetY: number } | null>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);

  const activeZone = zones.find((z) => z.id === activeZoneId) || zones[0];
  const zoneTables = tables.filter((t) => t.zoneId === activeZone?.id);

  // Canvas size - 1200x700 logical units (CSS px)
  const CANVAS_W = 1200;
  const CANVAS_H = 700;

  const onPointerDown = (e: React.PointerEvent, table: Table) => {
    e.preventDefault();
    if (!canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const scaleX = CANVAS_W / rect.width;
    const scaleY = CANVAS_H / rect.height;
    const px = (e.clientX - rect.left) * scaleX;
    const py = (e.clientY - rect.top) * scaleY;
    dragStateRef.current = {
      tableId: table.id,
      offsetX: px - parseFloat(table.posX || '0'),
      offsetY: py - parseFloat(table.posY || '0'),
    };
    setDraggingId(table.id);
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e: React.PointerEvent, table: Table) => {
    if (!dragStateRef.current || dragStateRef.current.tableId !== table.id) return;
    if (!canvasRef.current) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const scaleX = CANVAS_W / rect.width;
    const scaleY = CANVAS_H / rect.height;
    const px = (e.clientX - rect.left) * scaleX;
    const py = (e.clientY - rect.top) * scaleY;
    const w = parseFloat(table.width || '100');
    const h = parseFloat(table.height || '100');
    let newX = Math.round(px - dragStateRef.current.offsetX);
    let newY = Math.round(py - dragStateRef.current.offsetY);
    // Clamp within canvas
    newX = Math.max(0, Math.min(CANVAS_W - w, newX));
    newY = Math.max(0, Math.min(CANVAS_H - h, newY));
    // Optimistic local update during drag (no network)
    const el = e.currentTarget as HTMLElement;
    el.style.left = `${(newX / CANVAS_W) * 100}%`;
    el.style.top = `${(newY / CANVAS_H) * 100}%`;
    el.dataset.dragX = String(newX);
    el.dataset.dragY = String(newY);
  };

  const onPointerUp = (e: React.PointerEvent, table: Table) => {
    if (!dragStateRef.current || dragStateRef.current.tableId !== table.id) return;
    const el = e.currentTarget as HTMLElement;
    const finalX = parseFloat(el.dataset.dragX || table.posX || '0');
    const finalY = parseFloat(el.dataset.dragY || table.posY || '0');
    dragStateRef.current = null;
    setDraggingId(null);
    onMoveTable(table.id, finalX, finalY);
  };

  return (
    <div style={{ background: 'var(--color-bg-surface)', borderRadius: 10, border: '1px solid var(--color-border)', overflow: 'hidden' }}>
      {/* Zone tabs */}
      <div style={{ display: 'flex', gap: 4, padding: 8, background: 'var(--color-bg)', borderBottom: '1px solid var(--color-border)', overflowX: 'auto' }}>
        {zones.map((z) => {
          const count = tables.filter((t) => t.zoneId === z.id).length;
          const isActive = z.id === activeZone?.id;
          return (
            <button
              key={z.id}
              onClick={() => onSelectZone(z.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                padding: '8px 14px',
                border: 'none',
                background: isActive ? 'var(--color-bg-surface)' : 'transparent',
                color: isActive ? 'var(--color-text-strong)' : 'var(--color-text-muted)',
                fontSize: 12,
                fontWeight: 600,
                borderRadius: 6,
                cursor: 'pointer',
                whiteSpace: 'nowrap',
              }}
            >
              {z.name} <span style={{ opacity: 0.6 }}>({count})</span>
            </button>
          );
        })}
      </div>

      {/* Canvas */}
      <div
        ref={canvasRef}
        style={{
          position: 'relative',
          width: '100%',
          aspectRatio: `${CANVAS_W} / ${CANVAS_H}`,
          background: 'var(--color-bg)',
          backgroundImage: 'radial-gradient(circle, rgba(148,163,184,0.15) 1px, transparent 1px)',
          backgroundSize: '20px 20px',
          userSelect: 'none',
          touchAction: 'none',
        }}
      >
        {zoneTables.length === 0 && (
          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--color-text-muted)', fontSize: 13 }}>
            Nema stolova u zoni "{activeZone?.name}"
          </div>
        )}
        {zoneTables.map((t) => {
          const posX = parseFloat(t.posX || '0');
          const posY = parseFloat(t.posY || '0');
          const w = parseFloat(t.width || '100');
          const h = parseFloat(t.height || '100');
          const rotation = parseFloat(t.rotation || '0');
          const isRound = t.shape === 'round';
          const isDragging = draggingId === t.id;
          return (
            <div
              key={t.id}
              onPointerDown={(e) => onPointerDown(e, t)}
              onPointerMove={(e) => onPointerMove(e, t)}
              onPointerUp={(e) => onPointerUp(e, t)}
              onPointerCancel={(e) => onPointerUp(e, t)}
              onDoubleClick={() => onEditTable(t)}
              title={`${t.tableNumber} · ${t.name} · kapacitet ${t.capacity}. Drag da pomeris, dupli klik da menjaš.`}
              style={{
                position: 'absolute',
                left: `${(posX / CANVAS_W) * 100}%`,
                top: `${(posY / CANVAS_H) * 100}%`,
                width: `${(w / CANVAS_W) * 100}%`,
                height: `${(h / CANVAS_H) * 100}%`,
                transform: `rotate(${rotation}deg)`,
                background: t.isActive ? 'rgba(59,130,246,0.15)' : 'rgba(100,116,139,0.15)',
                border: `2px solid ${isDragging ? '#60a5fa' : t.isActive ? 'rgba(59,130,246,0.5)' : 'rgba(100,116,139,0.4)'}`,
                borderRadius: isRound ? '50%' : 8,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: isDragging ? 'grabbing' : 'grab',
                color: 'var(--color-text-strong)',
                fontSize: 12,
                fontWeight: 600,
                boxShadow: isDragging ? '0 8px 24px rgba(59,130,246,0.35)' : '0 2px 8px rgba(0,0,0,0.2)',
                transition: isDragging ? 'none' : 'box-shadow 0.15s',
              }}
            >
              <div style={{ fontSize: 14, fontWeight: 800, letterSpacing: '0.02em' }}>{t.tableNumber}</div>
              <div style={{ fontSize: 10, opacity: 0.75, display: 'flex', alignItems: 'center', gap: 3 }}>
                <Users size={9} /> {t.capacity}
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ padding: 10, borderTop: '1px solid var(--color-border)', background: 'var(--color-bg-surface)', fontSize: 11, color: 'var(--color-text-muted)', display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        <span><strong style={{ color: 'var(--color-text)' }}>Kako radi:</strong> Vuci stolove za pomeranje · Dupli klik za formu izmene · Canvas {CANVAS_W}×{CANVAS_H} jedinica</span>
        <div style={{ flex: 1 }} />
        <span>Stolova u zoni: <strong>{zoneTables.length}</strong></span>
      </div>
    </div>
  );
}

function ZoneFormDialog({ zone, locationId, onClose, onSaved }: { zone: Zone | null; locationId: string; onClose: () => void; onSaved: () => void }) {
  useEscapeToClose(onClose);
  const isEdit = !!zone;
  const [name, setName] = useState(zone?.name || '');
  const [code, setCode] = useState(zone?.code || '');
  const [sortOrder, setSortOrder] = useState(zone?.sortOrder ?? 0);
  const [isActive, setIsActive] = useState(zone?.isActive ?? true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) { setError('Naziv je obavezan'); return; }
    setSaving(true);
    setError(null);
    try {
      const body = { name: name.trim(), code: code.trim() || undefined, sortOrder, isActive };
      if (isEdit) {
        await api.put(`/admin/locations/${locationId}/zones/${zone!.id}`, body);
      } else {
        await api.post(`/admin/locations/${locationId}/zones`, body);
      }
      onSaved();
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Greška');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={overlay}>
      <div onClick={(e) => e.stopPropagation()} style={dialog}>
        <header style={dialogHeader}>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: 'var(--color-text-strong)' }}>
            {isEdit ? `Izmena zone: ${zone!.name}` : 'Nova zona'}
          </h3>
          <button onClick={onClose} style={closeBtn}><X size={16} /></button>
        </header>
        <form onSubmit={handleSubmit} style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Field label="Naziv *">
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Sala, Bašta, Bar..." autoFocus required style={input} />
          </Field>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 14 }}>
            <Field label="Code (opciono)">
              <input type="text" value={code} onChange={(e) => setCode(e.target.value)} placeholder="SAL, BAS..." style={{ ...input, fontFamily: 'monospace' }} />
            </Field>
            <Field label="Sort">
              <input type="number" value={sortOrder} onChange={(e) => setSortOrder(parseInt(e.target.value, 10) || 0)} style={input} />
            </Field>
          </div>
          <Field label="">
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
              <span style={{ fontSize: 13, color: 'var(--color-text)' }}>Aktivna</span>
            </label>
          </Field>
          {error && <div style={errorBox}><AlertTriangle size={14} /> {error}</div>}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 16, width: '100%', maxWidth: 360, marginLeft: 'auto', marginRight: 'auto' }}>
            <button type="button" onClick={onClose} style={{ ...btnSecondary, width: '100%', textAlign: 'center' }}>Otkaži</button>
            <button type="submit" disabled={saving} style={{ ...btnPrimary, width: '100%', justifyContent: 'center', opacity: saving ? 0.5 : 1 }}>
              <Save size={14} /> {saving ? 'Čuvanje...' : 'Sačuvaj'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function TableFormDialog({ table, zones, locationId, onClose, onSaved }: {
  table: Table | null;
  zones: Zone[];
  locationId: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  useEscapeToClose(onClose);
  const isEdit = !!table;
  const [zoneId, setZoneId] = useState(table?.zoneId || zones[0]?.id || '');
  const [name, setName] = useState(table?.name || '');
  const [tableNumber, setTableNumber] = useState(table?.tableNumber || '');
  const [capacity, setCapacity] = useState(table?.capacity ?? 2);
  const [minCapacity, setMinCapacity] = useState<number | ''>(table?.minCapacity ?? '');
  const [shape, setShape] = useState(table?.shape || 'square');
  const [serviceMode, setServiceMode] = useState(table?.serviceMode || 'dine_in');
  const [isActive, setIsActive] = useState(table?.isActive ?? true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!zoneId || !name.trim() || !tableNumber.trim()) {
      setError('Zona, naziv i broj stola su obavezni');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const body = {
        zoneId,
        name: name.trim(),
        tableNumber: tableNumber.trim(),
        capacity,
        minCapacity: minCapacity === '' ? null : minCapacity,
        shape,
        serviceMode,
        isActive,
      };
      if (isEdit) {
        await api.put(`/admin/locations/${locationId}/tables/${table!.id}`, body);
      } else {
        await api.post(`/admin/locations/${locationId}/tables`, body);
      }
      onSaved();
    } catch (e: any) {
      setError(e?.data?.message || e?.message || 'Greška');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={overlay}>
      <div onClick={(e) => e.stopPropagation()} style={dialog}>
        <header style={dialogHeader}>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: 'var(--color-text-strong)' }}>
            {isEdit ? `Izmena: ${table!.name}` : 'Novi sto'}
          </h3>
          <button onClick={onClose} style={closeBtn}><X size={16} /></button>
        </header>
        <form onSubmit={handleSubmit} style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Field label="Zona *">
            <select value={zoneId} onChange={(e) => setZoneId(e.target.value)} required style={input}>
              <option value="">- Izaberi zonu -</option>
              {zones.map((z) => <option key={z.id} value={z.id}>{z.name}</option>)}
            </select>
          </Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <Field label="Broj stola *">
              <input type="text" value={tableNumber} onChange={(e) => setTableNumber(e.target.value)} placeholder="S1, B3..." required autoFocus style={{ ...input, fontFamily: 'monospace' }} />
            </Field>
            <Field label="Naziv *">
              <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Sto 1, Bar 3..." required style={input} />
            </Field>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <Field label="Kapacitet *">
              <input type="number" min={1} value={capacity} onChange={(e) => setCapacity(parseInt(e.target.value, 10) || 1)} required style={input} />
            </Field>
            <Field label="Min kapacitet (opciono)">
              <input type="number" min={1} value={minCapacity} onChange={(e) => setMinCapacity(e.target.value === '' ? '' : parseInt(e.target.value, 10))} style={input} />
            </Field>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <Field label="Oblik">
              <select value={shape} onChange={(e) => setShape(e.target.value)} style={input}>
                <option value="square">Kvadrat</option>
                <option value="round">Krug</option>
                <option value="rect">Pravougaonik</option>
                <option value="custom">Custom</option>
              </select>
            </Field>
            <Field label="Service mode">
              <select value={serviceMode} onChange={(e) => setServiceMode(e.target.value)} style={input}>
                <option value="dine_in">Dine-in</option>
                <option value="bar">Bar</option>
                <option value="pickup_wait">Pickup wait</option>
              </select>
            </Field>
          </div>
          <Field label="">
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
              <span style={{ fontSize: 13, color: 'var(--color-text)' }}>Aktivan</span>
            </label>
          </Field>
          {error && <div style={errorBox}><AlertTriangle size={14} /> {error}</div>}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 16, width: '100%', maxWidth: 360, marginLeft: 'auto', marginRight: 'auto' }}>
            <button type="button" onClick={onClose} style={{ ...btnSecondary, width: '100%', textAlign: 'center' }}>Otkaži</button>
            <button type="submit" disabled={saving} style={{ ...btnPrimary, width: '100%', justifyContent: 'center', opacity: saving ? 0.5 : 1 }}>
              <Save size={14} /> {saving ? 'Čuvanje...' : 'Sačuvaj'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      {label && <label style={{ display: 'block', fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 4, fontWeight: 500 }}>{label}</label>}
      {children}
    </div>
  );
}

function statusBadge(status: string): React.CSSProperties {
  const colors: Record<string, { bg: string; text: string }> = {
    free: { bg: 'rgba(16,185,129,0.15)', text: '#6ee7b7' },
    occupied: { bg: 'rgba(59,130,246,0.15)', text: '#93c5fd' },
    reserved: { bg: 'rgba(245,158,11,0.15)', text: '#fcd34d' },
    dirty: { bg: 'rgba(168,85,247,0.15)', text: '#d8b4fe' },
    blocked: { bg: 'rgba(239,68,68,0.15)', text: '#fca5a5' },
    inactive: { bg: 'rgba(100,116,139,0.2)', text: '#94a3b8' },
  };
  const c = colors[status] || colors.free;
  return { fontSize: 10, padding: '2px 8px', borderRadius: 8, background: c.bg, color: c.text, fontWeight: 700 };
}

const th: React.CSSProperties = { padding: '10px 14px', fontSize: 11, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', textAlign: 'left', borderBottom: '1px solid var(--color-border)' };
const td: React.CSSProperties = { padding: '10px 14px', fontSize: 13, color: 'var(--color-text)', borderBottom: '1px solid var(--color-border-light)' };
const btnPrimary: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 6, border: 'none', background: 'var(--color-primary)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' };
const btnSecondary: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 6, background: 'var(--color-bg)', color: 'var(--color-text)', border: '1px solid var(--color-border)', fontSize: 13, fontWeight: 500, cursor: 'pointer' };
const iconBtn: React.CSSProperties = { background: 'transparent', border: 'none', color: 'var(--color-text-muted)', padding: 6, marginLeft: 4, borderRadius: 4, cursor: 'pointer' };
const errorBox: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 6, background: 'rgba(239,68,68,0.15)', color: '#fca5a5', fontSize: 13, marginBottom: 16 };
const overlay: React.CSSProperties = { position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.6)' };
const dialog: React.CSSProperties = { width: '100%', maxWidth: 520, background: 'var(--color-bg-surface)', borderRadius: 12, boxShadow: '0 20px 60px rgba(0,0,0,0.5)', border: '1px solid var(--color-border)' };
const dialogHeader: React.CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '18px 24px', borderBottom: '1px solid var(--color-border)' };
const closeBtn: React.CSSProperties = { background: 'transparent', border: 'none', color: 'var(--color-text-muted)', cursor: 'pointer', padding: 4 };
const input: React.CSSProperties = { width: '100%', padding: '9px 12px', background: 'var(--color-bg)', color: 'var(--color-text-strong)', border: '1px solid var(--color-border)', borderRadius: 6, fontSize: 13, outline: 'none' };
const badgeInactive: React.CSSProperties = { fontSize: 10, padding: '2px 8px', borderRadius: 8, background: 'rgba(100,116,139,0.2)', color: '#94a3b8', fontWeight: 700 };
const viewToggleBar: React.CSSProperties = { display: 'flex', gap: 4, padding: 4, background: 'var(--color-bg-surface)', border: '1px solid var(--color-border)', borderRadius: 8 };
const viewToggleBtn: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 5, padding: '6px 12px', border: 'none', background: 'transparent', color: 'var(--color-text-muted)', fontSize: 12, fontWeight: 600, borderRadius: 6, cursor: 'pointer' };
const viewToggleBtnActive: React.CSSProperties = { background: 'var(--color-bg)', color: 'var(--color-text-strong)' };
